import { useEffect, useRef, useState } from "react";
import { CensusFormType } from "../../../../utils/formType";
import { CensusInitialFormValues } from "../../../../utils/initialFormValues";
import CensusComponent from "./CensusComponent";
import {
  useUpdateOwnerAndEmployeeCensusMutation,
  useAddOwnerAndEmployeeCensusMutation,
  useDeleteOwnerAndEmployeeCensusMutation,
  EmployeeCensusSourceEnum,
} from "types/generated/operations";
import { getMemberUuid, getProspectUuid } from "utils/idHelpers";
import SavedCensus from "./SavedCensus";
import EditCard from "uicomponents/EditCard";
import FooterButton from "uicomponents/FooterButton";
import { EmployeeOfficeList, SingleCensusType } from "types";
import Alert from "uicomponents/Alert";
import {
  LOADING_BUTTON_TEXT,
  DELETE_BUTTON_TEXT,
  SAVE_BUTTON_TEXT,
} from "pages/WorkersCompApplcation/utils/contants";
import { EmployeeExemptionStatusEnum } from "types/generated/schema";
import { convertStringIntoBool, errorMessageMap } from "utils/helper";
import { trackFormError } from "utils/analytics/trackEvent";
import EditForm from "../../../../../../uicomponents/EditCard/EditForm";
import { ownerAndEmployeeValidationSchema } from "pages/WorkersCompApplcation/utils/ValidationSchema";
import SuggestedAddressModel from "uicomponents/SuggestedAddressModel";
import { BusinessAddressFormType } from "uicomponents/AddressComponent/FormUtils";

interface ICensusFormProps {
  data?: SingleCensusType;
  handleAddNewCensus: () => void;
  updateCensusList: (
    operation: string,
    owner?: SingleCensusType,
    uuid?: string
  ) => void;
  censusList?: SingleCensusType[];
  updateIsEditing: (isEditing: boolean) => void;
  officeLocations: EmployeeOfficeList;
}
const CensusForm = (props: ICensusFormProps): JSX.Element => {
  const {
    data,
    updateCensusList,
    censusList,
    updateIsEditing,
    handleAddNewCensus,
    officeLocations,
  } = props;

  const [suggestedAddresses, setSuggestedAddresses] = useState<
    BusinessAddressFormType[]
  >([]);
  const [isError, setIsError] = useState(false);
  const [isEditing, setIsEditing] = useState(data?.firstName ? false : true);
  const [errorMessage, setErrorMessage] = useState("");
  const [storedCensus, setStoredCensus] = useState<
    SingleCensusType | undefined
  >();

  useEffect(() => {
    if (!data?.officeLocationId && !data?.employeeInformation?.isRemote)
      setIsEditing(true);
  }, [data]);

  const isLastElement = censusList?.slice(-1)?.[0]?.uuid === data?.uuid;

  const isFirstUser = data?.uuid === getMemberUuid();
  const isFirstEdit =
    (isFirstUser &&
      data.firstName &&
      data.lastName &&
      data.email &&
      !data.employeeInformation?.title) ||
    false;
  const hasMoreCensus = (censusList?.length ?? 0) > 1;
  const isSavedToCensus =
    censusList?.some((census) => census.uuid === data?.uuid) || false;

  const initialFormValues: CensusFormType = CensusInitialFormValues(data);

  const [updateCensus, { loading: updateCensusLoading }] =
    useUpdateOwnerAndEmployeeCensusMutation();
  const [addCensus, { loading: addCensusLoading }] =
    useAddOwnerAndEmployeeCensusMutation();
  const [deleteCensus, { loading: deleteCensusLoading }] =
    useDeleteOwnerAndEmployeeCensusMutation();

  const toggleIsEditing = () => {
    updateIsEditing(!isEditing);
    setIsEditing(!isEditing);
  };

  // TODO: add dup modal
  const handleOnSubmit = async (values: CensusFormType) => {
    try {
      const isOwner = values.censusType === "Owner";
      const isEmployee = values.censusType === "Employee";
      const hasCensusInList = censusList?.find(
        (census: SingleCensusType) => census.uuid === values.uuid
      );

      let responseData;
      const isExistingCensus =
        (hasCensusInList && hasCensusInList.uuid !== "") ||
        suggestedAddresses.length > 0;

      const queryValues = {
        firstName: values.firstName,
        middleName: values.middleName,
        lastName: values.lastName,
        title: values.title,
        roleDescription: values.roleDescription,
        ownerStake: isOwner ? Number(values.ownerStake) : undefined,
        isPaid: isOwner ? values.isPaid : undefined,
        salary: Number(values.salary),
        email: values.email,
        employeeType: isEmployee ? values.employeeType : undefined,
        adminAccess: isEmployee
          ? convertStringIntoBool(values.adminAccess)
          : undefined,
        isCompanyOwner: values.censusType === "Owner",
        isCompanyEmployee: values.censusType === "Employee",
        salaryType: values.salaryType,
        hourlyRate: Number(values.hourlyRate),
        hoursWeekly: Number(values.hoursWeekly),
        officeLocationId: values.officeLocationId || undefined,
        exemptionStatus:
          (values.exemptionStatus
            ?.toUpperCase()
            .replace(/-/g, "_") as EmployeeExemptionStatusEnum) || null,
        isRemote: convertStringIntoBool(values.isRemote) || false,
        remoteAddress: convertStringIntoBool(values.isRemote)
          ? {
              address1: values.address1 || "",
              address2: values.address2,
              state: values.state || "",
              city: values.city || "",
              zip: values.zip || "",
            }
          : undefined,
      };

      if (isExistingCensus) {
        // update census
        const updateCensusResponse = await updateCensus({
          variables: {
            uuid:
              hasCensusInList?.uuid || values.uuid || storedCensus?.uuid || "",
            ...queryValues,
          },
        });

        responseData = updateCensusResponse.data?.updateEmployeeCensus;
      } else {
        // Add census
        const addCensusResponse = await addCensus({
          variables: {
            source: EmployeeCensusSourceEnum.WwWcApp,
            prospectId: getProspectUuid(),
            ...queryValues,
          },
        });
        responseData = addCensusResponse.data?.createEmployeeCensus;
      }

      const uuid = responseData?.uuid || "";
      const autoValidated = responseData?.addressAutoValidated;

      const newCensus = {
        source: EmployeeCensusSourceEnum.WwWcApp,
        uuid: uuid,
        firstName: values.firstName,
        middleName: values.middleName,
        lastName: values.lastName,
        email: values.email,
        officeLocationId: values.officeLocationId || undefined,
        isCompanyOwner: values.censusType === "Owner",
        isCompanyEmployee: values.censusType === "Employee",
        employeeType: isEmployee ? values.employeeType : "",
        employeeInformation: {
          title: values.title,
          adminAccess: isEmployee
            ? convertStringIntoBool(values.adminAccess)
            : undefined,
          roleDescription: values.roleDescription,
          ownerStake: isOwner ? Number(values.ownerStake) : undefined,
          isPaid: isOwner ? values.isPaid : undefined,
          salary: Number(values.salary),
          hourlyRate: Number(values.hourlyRate),
          hoursWeekly: Number(values.hoursWeekly),
          salaryType: values.salaryType,
          exemptionStatus:
            (values.exemptionStatus
              ?.toUpperCase()
              .replace(/-/g, "_") as EmployeeExemptionStatusEnum) || null,
          isRemote: convertStringIntoBool(values.isRemote) || false,
          remoteAddress: {
            address1: values.address1 || "",
            address2: values.address2 || "",
            state: values.state || "",
            city: values.city || "",
            zip: values.zip || "",
            autoValidated: autoValidated,
          },
        },
      };

      if (autoValidated === false && responseData?.suggestedAddress?.address1) {
        setSuggestedAddresses([
          {
            uuid: "",
            locationName: "",
            address1: values.address1 || "",
            address2: values.address2 || "",
            city: values.city || "",
            state: values.state || "",
            zip: values.zip || "",
          },
          {
            uuid: "",
            locationName: "",
            address1: responseData.suggestedAddress.address1 || "",
            address2: responseData.suggestedAddress.address2 || "",
            city: responseData.suggestedAddress.city || "",
            state: responseData.suggestedAddress.state || "",
            zip: responseData.suggestedAddress.zip || "",
          },
        ]);
        setStoredCensus(newCensus);
        return;
      }
      toggleIsEditing();
      updateCensusList("Add", newCensus);
      setIsError(false);
    } catch (error: any) {
      const errorMessage: string = error?.message;
      setError(error, errorMessage);
      return;
    }
  };

  const handleDeleteCensus = async () => {
    try {
      if (!data?.uuid) {
        updateCensusList("Delete", undefined, data?.uuid);
        return;
      }

      await deleteCensus({
        variables: {
          uuid: data.uuid,
        },
      });
      updateCensusList("Delete", undefined, data.uuid);
      setIsError(false);
    } catch (error: any) {
      const errorMessage: string = error?.message;
      setError(error, errorMessage);
      return;
    }
  };

  const setError = (error: Error, errorMessage: string) => {
    trackFormError(error, errorMessage);

    errorMessageMap[errorMessage] &&
      setErrorMessage(errorMessageMap[errorMessage]);
    setIsError(true);
  };

  const handleKeepCurrent = () => {
    if (storedCensus) {
      toggleIsEditing();
      setSuggestedAddresses([]);
      updateCensusList("Add", storedCensus);
      setIsError(false);
    }
  };

  const handleUseSuggestion = (suggestedAddress: BusinessAddressFormType) => {
    setSuggestedAddresses([]);
    handleOnSubmit({
      censusType: storedCensus?.isCompanyOwner ? "Owner" : "Employee",
      firstName: storedCensus?.firstName || "",
      middleName: storedCensus?.middleName || "",
      lastName: storedCensus?.lastName || "",
      title: storedCensus?.employeeInformation?.title || "",
      employeeType: storedCensus?.employeeType || "",
      roleDescription: storedCensus?.employeeInformation?.roleDescription || "",
      ownerStake: String(storedCensus?.employeeInformation?.ownerStake) ?? "",
      isPaid: storedCensus?.employeeInformation?.isPaid || "",
      salary: String(storedCensus?.employeeInformation?.salary) || "",
      hourlyRate: String(storedCensus?.employeeInformation?.hourlyRate) || "",
      hoursWeekly: String(storedCensus?.employeeInformation?.hoursWeekly) || "",
      salaryType: storedCensus?.employeeInformation?.salaryType || "",
      uuid: storedCensus?.uuid || "",
      exemptionStatus: storedCensus?.employeeInformation?.exemptionStatus || "",

      email: storedCensus?.email || "",
      adminAccess: storedCensus?.employeeInformation?.adminAccess
        ? "Yes"
        : "No",

      isRemote: storedCensus?.employeeInformation?.isRemote ? "Yes" : "No",
      officeLocationId: storedCensus?.officeLocationId || "",
      address1: suggestedAddress.address1,
      address2: suggestedAddress.address2,
      city: suggestedAddress.city,
      state: suggestedAddress.state,
      zip: suggestedAddress.zip,
    });
  };

  const fieldRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  // Helper function that makes the browser scroll a specific field into view
  const focusField = (fieldName: string) => {
    if (fieldRefs.current[fieldName]) {
      fieldRefs.current[fieldName]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  return (
    <>
      {suggestedAddresses.length === 2 && (
        <SuggestedAddressModel
          enteredAddress={suggestedAddresses[0]}
          suggestedAddress={suggestedAddresses[1]}
          onUseSuggestion={handleUseSuggestion}
          onKeepCurrent={handleKeepCurrent}
        />
      )}
      {isEditing && (
        <EditForm
          initialFormValues={initialFormValues}
          validationSchema={ownerAndEmployeeValidationSchema}
          handleOnSubmit={handleOnSubmit}
          title={isFirstUser ? "Add yourself" : "Team member"}
          description={
            isFirstUser ? (
              <>
                Since you previously identified yourself as an{" "}
                <b>{initialFormValues.censusType.toLowerCase()}</b>, we’ve gone
                ahead and added some of the information you’ve already given us.
              </>
            ) : (
              ""
            )
          }
          nextButtonText={
            updateCensusLoading || addCensusLoading
              ? LOADING_BUTTON_TEXT
              : SAVE_BUTTON_TEXT
          }
          handleBackClick={() => toggleIsEditing()}
          disabled={updateCensusLoading || addCensusLoading}
          isError={isError}
          errorMessage={errorMessage}
          hasOnlyNextButton={isFirstEdit}
          focusField={focusField}
        >
          <CensusComponent
            officeLocations={officeLocations}
            isSavedToCensus={isSavedToCensus}
            fieldRefs={fieldRefs}
          />
        </EditForm>
      )}
      {!isEditing && (
        <EditCard
          title={isFirstUser ? "Add yourself" : "Team member"}
          onEditClick={() => toggleIsEditing()}
          description={
            isFirstUser ? (
              <>
                Since you previously identified yourself as an{" "}
                <b>{initialFormValues.censusType.toLowerCase()}</b>, we’ve gone
                ahead and added some of the information you’ve already given us.
              </>
            ) : (
              ""
            )
          }
        >
          <SavedCensus data={data} officeLocations={officeLocations} />
          {hasMoreCensus && isFirstUser ? (
            <></>
          ) : (
            <FooterButton
              nextButtonText={
                deleteCensusLoading
                  ? LOADING_BUTTON_TEXT
                  : "Add new team member"
              }
              hasOnlyBackButton={!isLastElement && !isFirstUser}
              hasOnlyNextButton={isFirstUser}
              backButtonText={DELETE_BUTTON_TEXT}
              handleBackClick={handleDeleteCensus}
              handleNextClick={handleAddNewCensus}
              disabled={deleteCensusLoading}
            />
          )}
          {isError && <Alert message={errorMessage} allowAutoScroll={false} />}
        </EditCard>
      )}
    </>
  );
};

export default CensusForm;
